/**
 * Configuration — Appeals — source modules, decision types, account coverage, sample rules
 */

export const APPEALS_CONFIG_ROUTE = '/admin/configuration/appeals';

export const PLATFORM_APPEAL_WINDOW = 'Fixed 7 calendar days';
export const PLATFORM_APPEAL_WINDOW_STARTS =
  'Seven calendar days from the original notice’s first availability, through the inclusive deadline.';
export const PLATFORM_APPEAL_WINDOW_DEADLINE =
  'The same local clock time seven calendar dates later in the recorded time zone. Weekends and holidays count.';
export const PLATFORM_APPEAL_OUTCOMES = ['Appeal Approved', 'Appeal Rejected'];
export const PLATFORM_APPEAL_OUTCOMES_DISPLAY = 'Appeal Approved; Appeal Rejected';
export const PLATFORM_REJECTED_BEHAVIOUR =
  'Does not authorise the approved corrective action. A correction follows operational reversal requirements.';
export const PLATFORM_ELIGIBLE_DECISION =
  'A supported final adverse decision against the affected authenticated Customer or Business.';
export const PLATFORM_APPEAL_LIMIT =
  'One Appeal per actual original decision, including a Withdrawn Appeal.';
export const PLATFORM_WITHDRAWN_NOTE =
  'A pre-decision operational status, not a decision outcome; it does not permit another Appeal for the same decision.';
export const PLATFORM_APPLIED_SETTINGS_NOTE =
  'At successful operational submission, retain the current Rule ID and complete applied settings, including scope, account coverage, availability, action, Conditions and fixed window.';

export const APPEAL_AVAILABILITY_OPTIONS = [
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

export const ACCOUNT_TYPE_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'business', label: 'Business' },
];

export const ACCOUNT_TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'customer', label: 'Customer' },
  { value: 'business', label: 'Business' },
  { value: 'both', label: 'Customer & Business' },
];

export const APPEAL_AVAILABILITY_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

export const PLATFORM_DEPENDENCIES = {
  appealSubmissionForm: { label: 'Appeal Submission Form', available: true },
  appealReplyForm: { label: 'Appeal Reply Form', available: true },
  appealDecisionForm: { label: 'Appeal Decision Form', available: true },
  formInventoryValidation: { label: 'Appeal form configuration', valid: true },
  sharedMessageEditor: { label: 'Shared Message Editor', available: true },
};

/** Corrective-action meanings shown read-only on View / Screen 2. */
export const CORRECTIVE_ACTION_MEANINGS = {
  overturn_post_decision:
    'Remove the challenged decision’s publication/visibility restriction and restore eligible existing Post content under the owner’s current requirements. A Post previously unpublished to Draft is handled through that owner. Cancel only warnings or restrictions actually attached to that challenged decision.',
  overturn_review_decision:
    'Remove the challenged Review restriction and restore eligible existing content under current owner requirements. Cancel only warnings or restrictions actually attached to that challenged decision.',
  overturn_reply_decision:
    'Remove the challenged Business Reply restriction and restore eligible existing content, respecting parent Review visibility. Cancel only warnings or restrictions actually attached to that challenged decision.',
  overturn_profile_decision:
    'Remove the challenged Business Profile Content restriction and restore eligible existing content under current owner requirements. Cancel only warnings or restrictions actually attached to that challenged decision.',
  overturn_message_decision:
    'Cancel the challenged adverse finding’s applicable consequences, warnings or restrictions. Do not assume messages were hidden, recreate content or restart an ended chat pause.',
  overturn_quote_decision:
    'Cancel the challenged adverse finding’s applicable consequences, warnings or restrictions. Do not assume messages were hidden, recreate content or restart an ended chat pause.',
  approve_licence_application:
    'Apply the corresponding existing approval requirements and create or update the Business Compliance Record as applicable.',
  approve_badge_application:
    'Apply the corresponding existing approval requirements and create or update the Business Compliance Record as applicable.',
  approve_ticket_application:
    'Apply the corresponding existing approval requirements and create or update the Business Compliance Record as applicable.',
  cancel_final_warning:
    'Cancel that warning’s effect so it no longer counts as an applicable previous warning. Retain its history.',
  lift_permanent_ban:
    'Remove the challenged ban and the associated public-content hiding caused by that ban, subject to current source controls and restoration eligibility. Independent bans or hiding remain effective.',
};

function decisionDef(partial) {
  return {
    available: true,
    complete: true,
    eligible: true,
    windowSupport: true,
    accountTypes: ['customer', 'business'],
    approvedActions: [],
    ...partial,
  };
}

function actionDef(value, label, accountTypes) {
  return { value, label, accountTypes, available: true };
}

/**
 * Supported catalogue: Source Module → Decision Type → Account Types → Corrective Action.
 * Moderation labels combine case area with Confirmed outcome.
 */
export const DECISION_TYPE_OPTIONS = [
  decisionDef({
    value: 'post_confirmed',
    label: 'Post — Confirmed',
    sourceModule: 'moderation',
    accountTypes: ['business'],
    approvedActions: [
      actionDef('overturn_post_decision', 'Overturn Post Decision', ['business']),
    ],
  }),
  decisionDef({
    value: 'review_confirmed',
    label: 'Review — Confirmed',
    sourceModule: 'moderation',
    accountTypes: ['customer'],
    approvedActions: [
      actionDef('overturn_review_decision', 'Overturn Review Decision', ['customer']),
    ],
  }),
  decisionDef({
    value: 'business_reply_confirmed',
    label: 'Business Reply — Confirmed',
    sourceModule: 'moderation',
    accountTypes: ['business'],
    approvedActions: [
      actionDef('overturn_reply_decision', 'Overturn Reply Decision', ['business']),
    ],
  }),
  decisionDef({
    value: 'business_profile_content_confirmed',
    label: 'Business Profile Content — Confirmed',
    sourceModule: 'moderation',
    accountTypes: ['business'],
    approvedActions: [
      actionDef('overturn_profile_decision', 'Overturn Profile Decision', ['business']),
    ],
  }),
  decisionDef({
    value: 'message_confirmed',
    label: 'Message — Confirmed',
    sourceModule: 'moderation',
    accountTypes: ['customer', 'business'],
    approvedActions: [
      actionDef('overturn_message_decision', 'Overturn Message Decision', ['customer', 'business']),
    ],
  }),
  decisionDef({
    value: 'quote_confirmed',
    label: 'Quote — Confirmed',
    sourceModule: 'moderation',
    accountTypes: ['customer', 'business'],
    approvedActions: [
      actionDef('overturn_quote_decision', 'Overturn Quote Decision', ['customer', 'business']),
    ],
  }),
  decisionDef({
    value: 'licence_rejected',
    label: 'Licence Rejected',
    sourceModule: 'compliance',
    accountTypes: ['business'],
    approvedActions: [
      actionDef('approve_licence_application', 'Approve Licence Application', ['business']),
    ],
  }),
  decisionDef({
    value: 'badge_rejected',
    label: 'Badge Rejected',
    sourceModule: 'compliance',
    accountTypes: ['business'],
    approvedActions: [
      actionDef('approve_badge_application', 'Approve Badge Application', ['business']),
    ],
  }),
  decisionDef({
    value: 'trade_ticket_rejected',
    label: 'Trade Ticket Rejected',
    sourceModule: 'compliance',
    accountTypes: ['business'],
    approvedActions: [
      actionDef('approve_ticket_application', 'Approve Ticket Application', ['business']),
    ],
  }),
  decisionDef({
    value: 'final_warning',
    label: 'Final Warning',
    sourceModule: 'fraud_security',
    accountTypes: ['customer', 'business'],
    approvedActions: [
      actionDef('cancel_final_warning', 'Cancel Final Warning', ['customer', 'business']),
    ],
  }),
  decisionDef({
    value: 'permanent_ban',
    label: 'Permanent Ban',
    sourceModule: 'fraud_security',
    accountTypes: ['customer', 'business'],
    approvedActions: [
      actionDef('lift_permanent_ban', 'Lift Permanent Ban', ['customer', 'business']),
    ],
  }),
];

export const SOURCE_MODULE_OPTIONS = [
  { value: 'moderation', label: 'Moderation' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'fraud_security', label: 'Fraud & Security' },
].filter((module) =>
  DECISION_TYPE_OPTIONS.some(
    (decision) => decision.sourceModule === module.value && decision.eligible !== false
  )
);

export const SOURCE_MODULE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Source Modules' },
  ...SOURCE_MODULE_OPTIONS,
];

export function labelFromOptions(options, value) {
  if (value == null || value === '') return '';
  return options.find((option) => option.value === value)?.label ?? String(value);
}

export function getSourceModuleLabel(value) {
  return labelFromOptions(SOURCE_MODULE_OPTIONS, value) || (value ? `${value} Unavailable` : '');
}

export function getAppealAvailabilityLabel(value) {
  return labelFromOptions(APPEAL_AVAILABILITY_OPTIONS, value);
}

export function getDecisionDefinition(sourceModule, decisionType) {
  return (
    DECISION_TYPE_OPTIONS.find(
      (decision) =>
        decision.value === decisionType && (!sourceModule || decision.sourceModule === sourceModule)
    ) || null
  );
}

export function getDecisionTypeOptionsForModule(sourceModule) {
  if (!sourceModule) return [];
  return DECISION_TYPE_OPTIONS.filter(
    (decision) => decision.sourceModule === sourceModule && decision.eligible !== false
  ).map((decision) => ({
    value: decision.value,
    label: decision.available === false ? `${decision.label} Unavailable` : decision.label,
    available: decision.available !== false,
  }));
}

export function getDecisionTypeLabel(sourceModule, decisionType) {
  const definition = getDecisionDefinition(sourceModule, decisionType);
  if (!definition) return decisionType ? `${decisionType} Unavailable` : '';
  return definition.available === false ? `${definition.label} Unavailable` : definition.label;
}

export function getApprovedActionOptions(sourceModule, decisionType, accountTypes) {
  const types = normalizeAccountTypes(accountTypes);
  if (!sourceModule || !decisionType || !types.length) return [];
  const definition = getDecisionDefinition(sourceModule, decisionType);
  if (!definition) return [];
  return (definition.approvedActions || [])
    .filter((action) => {
      const supported = action.accountTypes || definition.accountTypes || [];
      return types.every((type) => supported.includes(type));
    })
    .map((action) => ({
      value: action.value,
      label: action.available === false ? `${action.label} Unavailable` : action.label,
      available: action.available !== false,
    }));
}

export function getApprovedActionLabel(sourceModule, decisionType, approvedAction) {
  const definition = getDecisionDefinition(sourceModule, decisionType);
  const action = (definition?.approvedActions || []).find((item) => item.value === approvedAction);
  if (!action) {
    // Fall back to known meanings catalogue label if reference survives a definition change
    const known = Object.entries(CORRECTIVE_ACTION_MEANINGS).find(([value]) => value === approvedAction);
    if (known) {
      const fromCatalogue = DECISION_TYPE_OPTIONS.flatMap((d) => d.approvedActions || []).find(
        (a) => a.value === approvedAction
      );
      return fromCatalogue
        ? `${fromCatalogue.label} Unavailable`
        : `${approvedAction} Unavailable`;
    }
    return approvedAction ? `${approvedAction} Unavailable` : '';
  }
  return action.available === false ? `${action.label} Unavailable` : action.label;
}

export function getCorrectiveActionMeaning(approvedAction) {
  if (!approvedAction) return '—';
  return CORRECTIVE_ACTION_MEANINGS[approvedAction] || '—';
}

export function getAccountTypeLabel(value) {
  return labelFromOptions(ACCOUNT_TYPE_OPTIONS, value);
}

export function normalizeAccountTypes(accountTypes) {
  if (!Array.isArray(accountTypes)) return [];
  const allowed = new Set(ACCOUNT_TYPE_OPTIONS.map((option) => option.value));
  return [...new Set(accountTypes.filter((value) => allowed.has(value)))];
}

export function accountTypesKey(accountTypes) {
  return normalizeAccountTypes(accountTypes).slice().sort().join('|');
}

export function accountTypesOverlap(a, b) {
  const left = new Set(normalizeAccountTypes(a));
  return normalizeAccountTypes(b).some((type) => left.has(type));
}

export function getAccountTypeDisplay(accountTypes) {
  const types = normalizeAccountTypes(accountTypes);
  const hasCustomer = types.includes('customer');
  const hasBusiness = types.includes('business');
  if (hasCustomer && hasBusiness) return 'Customer & Business';
  if (hasCustomer) return 'Customer';
  if (hasBusiness) return 'Business';
  return '—';
}

export function getAccountTypeFilterKey(accountTypes) {
  const types = normalizeAccountTypes(accountTypes);
  const hasCustomer = types.includes('customer');
  const hasBusiness = types.includes('business');
  if (hasCustomer && hasBusiness) return 'both';
  if (hasCustomer) return 'customer';
  if (hasBusiness) return 'business';
  return '';
}

/** Table / form short summary (Notice · Email · …). */
export function getGeneratedAccessSummary(accountTypes) {
  const types = normalizeAccountTypes(accountTypes);
  const hasCustomer = types.includes('customer');
  const hasBusiness = types.includes('business');
  if (hasCustomer && hasBusiness) return 'Notice · Email · Applicable Dashboard';
  if (hasBusiness) return 'Notice · Email · Business Dashboard';
  if (hasCustomer) return 'Notice · Email · Customer Dashboard';
  return '—';
}

export function getGeneratedAccessDetail(accountTypes) {
  const types = normalizeAccountTypes(accountTypes);
  const hasCustomer = types.includes('customer');
  const hasBusiness = types.includes('business');
  const parts = ['Decision Notification Link', 'Decision Email Link'];
  if (hasCustomer && hasBusiness) parts.push('the affected account’s applicable Dashboard — Appeals');
  else if (hasBusiness) parts.push('Business Dashboard — Appeals');
  else if (hasCustomer) parts.push('Customer Dashboard — Appeals');
  return parts.join('; ');
}

export function formatAppealsDate(value) {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = date.getUTCDate();
  const month = date.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });
  const year = date.getUTCFullYear();
  return `${day} ${month} ${year}`;
}

export function formatAppealsDateTime(value) {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const hours24 = date.getUTCHours();
  const hours12 = hours24 % 12 || 12;
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const suffix = hours24 >= 12 ? 'pm' : 'am';
  return `${formatAppealsDate(value)}, ${hours12}:${minutes} ${suffix}`;
}

export function getDependencyAvailability(dependencies = PLATFORM_DEPENDENCIES) {
  const source = dependencies || PLATFORM_DEPENDENCIES;
  return {
    appealSubmissionForm: source.appealSubmissionForm?.available !== false,
    appealReplyForm: source.appealReplyForm?.available !== false,
    appealDecisionForm: source.appealDecisionForm?.available !== false,
    formInventoryValidation: source.formInventoryValidation?.valid !== false,
    sharedMessageEditor: source.sharedMessageEditor?.available !== false,
  };
}

export function areEnabledDependenciesReady(dependencies = PLATFORM_DEPENDENCIES) {
  const availability = getDependencyAvailability(dependencies);
  return (
    availability.appealSubmissionForm &&
    availability.appealReplyForm &&
    availability.appealDecisionForm &&
    availability.formInventoryValidation &&
    availability.sharedMessageEditor
  );
}

export function normalizeAppealAvailability(value, fallback = '') {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'enabled' || normalized === 'active') return 'enabled';
  if (normalized === 'disabled' || normalized === 'inactive') return 'disabled';
  return fallback;
}

export function createEmptyAppealForm() {
  return {
    id: '',
    sourceModule: '',
    decisionType: '',
    accountTypes: [],
    approvedCorrectiveAction: '',
    appealAvailability: '',
    conditions: [],
    dateAdded: '',
    addedBy: '',
    lastUpdated: '',
    lastUpdatedBy: '',
  };
}

export function applyAppealSelectionChange(form, field, value) {
  const next = { ...form, [field]: value };
  if (field === 'sourceModule') {
    next.decisionType = '';
    next.approvedCorrectiveAction = '';
  }
  if (field === 'decisionType') {
    next.approvedCorrectiveAction = '';
  }
  if (field === 'accountTypes') {
    const actions = getApprovedActionOptions(
      next.sourceModule,
      next.decisionType,
      next.accountTypes
    );
    if (
      next.approvedCorrectiveAction &&
      !actions.some((action) => action.value === next.approvedCorrectiveAction)
    ) {
      next.approvedCorrectiveAction = '';
    }
  }
  return next;
}

export function normalizeAppealForm(raw = {}) {
  const sourceModule = raw.sourceModule || '';
  const decisionType = raw.decisionType || '';
  const approvedCorrectiveAction = raw.approvedCorrectiveAction || raw.approvedAction || '';
  return {
    ...createEmptyAppealForm(),
    ...raw,
    sourceModule,
    decisionType,
    accountTypes: normalizeAccountTypes(raw.accountTypes),
    approvedCorrectiveAction,
    approvedAction: approvedCorrectiveAction,
    appealAvailability: normalizeAppealAvailability(raw.appealAvailability || raw.status, ''),
    conditions: Array.isArray(raw.conditions) ? raw.conditions : [],
    dateAdded: raw.dateAdded || raw.createdOn || '',
    addedBy: raw.addedBy || raw.createdBy || '',
    lastUpdated: raw.lastUpdated || raw.modifiedOn || '',
    lastUpdatedBy: raw.lastUpdatedBy || raw.modifiedBy || '',
    platformDependencies: raw.platformDependencies || { ...PLATFORM_DEPENDENCIES },
  };
}

export function nextAppealRuleId(allRules) {
  const nums = (allRules || []).map((rule) => {
    const match = String(rule.id || '').match(/^APC-(\d+)$/i);
    return match ? parseInt(match[1], 10) : 0;
  });
  const max = nums.length ? Math.max(...nums) : 0;
  return `APC-${String(max + 1).padStart(4, '0')}`;
}

export function appealScopeKey(rule) {
  const form = normalizeAppealForm(rule);
  return [form.sourceModule, form.decisionType, accountTypesKey(form.accountTypes)].join('|');
}

export function findDuplicateAppealRule(rules, candidate, excludeId) {
  const form = normalizeAppealForm(candidate);
  const coverage = appealScopeKey(form);
  return (
    (rules || []).map(normalizeAppealForm).find(
      (rule) =>
        rule.id !== excludeId &&
        appealScopeKey(rule) === coverage &&
        rule.approvedCorrectiveAction === form.approvedCorrectiveAction &&
        rule.appealAvailability === form.appealAvailability
    ) || null
  );
}

export function findOverlappingAppealRule(rules, candidate, excludeId) {
  const form = normalizeAppealForm(candidate);
  if (!form.sourceModule || !form.decisionType || !form.accountTypes.length) return null;
  return (
    (rules || []).map(normalizeAppealForm).find((rule) => {
      if (rule.id === excludeId) return false;
      if (rule.sourceModule !== form.sourceModule || rule.decisionType !== form.decisionType) {
        return false;
      }
      return accountTypesOverlap(rule.accountTypes, form.accountTypes);
    }) || null
  );
}

export function sortAppealRules(rules = []) {
  return [...rules].sort((a, b) => {
    const aTime = new Date(a.lastUpdated || a.dateAdded || 0).getTime();
    const bTime = new Date(b.lastUpdated || b.dateAdded || 0).getTime();
    if (bTime !== aTime) return bTime - aTime;
    return String(a.id || '').localeCompare(String(b.id || ''), undefined, { numeric: true });
  });
}

function actorFields(added, updated) {
  return {
    addedBy: 'Sample Admin',
    lastUpdatedBy: 'Sample Admin',
    dateAdded: added,
    lastUpdated: updated,
  };
}

/** Table 1 interface examples from the Configuration — Appeals specification. */
export function buildSampleAppealRules() {
  return [
    {
      id: 'APC-0006',
      sourceModule: 'compliance',
      decisionType: 'trade_ticket_rejected',
      accountTypes: ['business'],
      approvedCorrectiveAction: 'approve_ticket_application',
      appealAvailability: 'disabled',
      ...actorFields('2026-07-22T09:00:00.000Z', '2026-08-08T13:35:00.000Z'),
    },
    {
      id: 'APC-0005',
      sourceModule: 'compliance',
      decisionType: 'badge_rejected',
      accountTypes: ['business'],
      approvedCorrectiveAction: 'approve_badge_application',
      appealAvailability: 'enabled',
      ...actorFields('2026-07-22T09:00:00.000Z', '2026-08-08T13:30:00.000Z'),
    },
    {
      id: 'APC-0004',
      sourceModule: 'compliance',
      decisionType: 'licence_rejected',
      accountTypes: ['business'],
      approvedCorrectiveAction: 'approve_licence_application',
      appealAvailability: 'enabled',
      ...actorFields('2026-07-21T09:00:00.000Z', '2026-08-08T11:15:00.000Z'),
    },
    {
      id: 'APC-0003',
      sourceModule: 'moderation',
      decisionType: 'review_confirmed',
      accountTypes: ['customer'],
      approvedCorrectiveAction: 'overturn_review_decision',
      appealAvailability: 'enabled',
      ...actorFields('2026-07-21T09:00:00.000Z', '2026-08-08T11:10:00.000Z'),
    },
    {
      id: 'APC-0002',
      sourceModule: 'moderation',
      decisionType: 'post_confirmed',
      accountTypes: ['business'],
      approvedCorrectiveAction: 'overturn_post_decision',
      appealAvailability: 'enabled',
      ...actorFields('2026-07-20T09:00:00.000Z', '2026-08-08T10:25:00.000Z'),
    },
    {
      id: 'APC-0001',
      sourceModule: 'fraud_security',
      decisionType: 'permanent_ban',
      accountTypes: ['customer', 'business'],
      approvedCorrectiveAction: 'lift_permanent_ban',
      appealAvailability: 'enabled',
      ...actorFields('2026-07-20T09:00:00.000Z', '2026-08-08T10:20:00.000Z'),
    },
  ].map((rule) => normalizeAppealForm(rule));
}

export function matchesAppealsSearch(rule, searchTerm) {
  if (!searchTerm || !String(searchTerm).trim()) return true;
  const term = String(searchTerm).toLowerCase().trim();
  const form = normalizeAppealForm(rule);
  return (
    String(form.id || '')
      .toLowerCase()
      .includes(term) ||
    getSourceModuleLabel(form.sourceModule).toLowerCase().includes(term) ||
    getDecisionTypeLabel(form.sourceModule, form.decisionType).toLowerCase().includes(term)
  );
}

export function matchesAppealsFilters(rule, { sourceModule, accountType, appealAvailability } = {}) {
  const form = normalizeAppealForm(rule);
  if (sourceModule && sourceModule !== 'all' && form.sourceModule !== sourceModule) return false;
  if (appealAvailability && appealAvailability !== 'all' && form.appealAvailability !== appealAvailability) {
    return false;
  }
  if (accountType && accountType !== 'all') {
    const key = getAccountTypeFilterKey(form.accountTypes);
    if (accountType === 'both') {
      if (key !== 'both') return false;
    } else if (accountType === 'customer') {
      // Customer includes Customer-only and combined Rules
      if (key !== 'customer' && key !== 'both') return false;
    } else if (accountType === 'business') {
      // Business includes Business-only and combined Rules
      if (key !== 'business' && key !== 'both') return false;
    }
  }
  return true;
}

/** @deprecated aliases */
export const PLATFORM_APPEAL_WINDOW_SHORT = PLATFORM_APPEAL_WINDOW;
export const PLATFORM_HANDLING_TEAM = '';
export const PLATFORM_HANDLING_TEAM_SHORT = '';
export const PLATFORM_ASSIGNMENT_RULE = '';
export const CONFIG_STATUS = { ACTIVE: 'enabled', INACTIVE: 'disabled' };
export const STATUS_FILTER_OPTIONS = APPEAL_AVAILABILITY_FILTER_OPTIONS;
export const SOURCE_MODULE_DECISION_MAP = SOURCE_MODULE_OPTIONS.reduce((map, module) => {
  map[module.value] = DECISION_TYPE_OPTIONS.filter(
    (decision) => decision.sourceModule === module.value
  );
  return map;
}, {});
export const MESSAGES = {
  empty: 'No appeal rules found',
  saved: 'Appeal Rule saved.',
};

export function normalizeAppealsStatus(status) {
  return getAppealAvailabilityLabel(normalizeAppealAvailability(status, ''));
}

export function getAccessSummary(accountTypes) {
  return getGeneratedAccessSummary(accountTypes);
}

export function getAccessDetailRows(accountTypes) {
  return [{ label: 'Generated Access', value: getGeneratedAccessDetail(accountTypes) }];
}

export function getAccessDetailSummary(accountTypes) {
  return getGeneratedAccessDetail(accountTypes);
}

export function arePlatformDependenciesReady(dependencies = PLATFORM_DEPENDENCIES) {
  return areEnabledDependenciesReady(dependencies);
}

export function normalizeAppealConfiguration(raw) {
  return normalizeAppealForm(raw);
}

export function createEmptyAppealConfiguration() {
  return createEmptyAppealForm();
}

export function nextAppealConfigId(configurations) {
  return nextAppealRuleId(configurations);
}

export function findOverlappingActiveConfiguration(configurations, candidate, excludeId) {
  return findOverlappingAppealRule(configurations, candidate, excludeId);
}

export function getListActionsForConfiguration() {
  return [
    { key: 'view', label: 'View' },
    { key: 'edit', label: 'Edit' },
  ];
}

export function buildSampleAppealConfigurations() {
  return buildSampleAppealRules();
}
